import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, XCircle, Clock, BarChart3, PieChart, Calendar, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom_date', label: 'Specific Date' },
  { value: 'custom_month', label: 'Specific Month' },
  { value: 'custom_year', label: 'Specific Year' },
];

const fmtAmt = (amount, currency = 'KES') => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const STATUS_LABELS = {
  pending_review: 'Pending Review',
  pending_authorization: 'Pending Authorization',
  pending_disbursement: 'Pending Disbursement',
  completed: 'Disbursed',
  rejected: 'Rejected',
  returned: 'Returned',
};

const STATUS_COLORS = {
  pending_review: 'bg-amber-500',
  pending_authorization: 'bg-orange-500',
  pending_disbursement: 'bg-blue-500',
  completed: 'bg-emerald-500',
  rejected: 'bg-red-500',
  returned: 'bg-zinc-400',
};

const ExpenseAnalytics = () => {
  const { getAuthHeaders } = useAuth();
  const [period, setPeriod] = useState('this_month');
  const [customDate, setCustomDate] = useState('');
  const [customMonth, setCustomMonth] = useState('');
  const [customYear, setCustomYear] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/expenses/analytics/summary?period=${period.startsWith('custom') ? 'custom' : period}`;
      if (period === 'custom_date' && customDate) url += `&custom_date=${customDate}`;
      if (period === 'custom_month' && customMonth) url += `&custom_month=${customMonth}`;
      if (period === 'custom_year' && customYear) url += `&custom_year=${customYear}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to load analytics');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, [period, customDate, customMonth, customYear, getAuthHeaders]);

  useEffect(() => {
    // Don't fetch for custom periods without a value
    if (period === 'custom_date' && !customDate) return;
    if (period === 'custom_month' && !customMonth) return;
    if (period === 'custom_year' && !customYear) return;
    fetchAnalytics();
  }, [fetchAnalytics, period, customDate, customMonth, customYear]);

  const w = data?.widgets || {};
  const currency = data?.currency || 'KES';

  return (
    <div className="space-y-6" data-testid="expense-analytics-page">
      {/* Period filter */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="period-filter">
          <select
            className="h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="period-select"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {period === 'custom_date' && (
            <input
              type="date"
              className="h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              data-testid="custom-date-input"
            />
          )}
          {period === 'custom_month' && (
            <input
              type="month"
              className="h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm"
              value={customMonth}
              onChange={(e) => setCustomMonth(e.target.value)}
              data-testid="custom-month-input"
            />
          )}
          {period === 'custom_year' && (
            <select
              className="h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm"
              value={customYear}
              onChange={(e) => setCustomYear(e.target.value)}
              data-testid="custom-year-input"
            >
              <option value="">Select year</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : (
        <>
          {/* ── Widget Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="widget-cards">
            <WidgetCard
              label="Total Requests"
              value={`${currency} ${fmtAmt(w.total_requests)}`}
              sub={`${w.total_count || 0} requests`}
              icon={DollarSign}
              color="blue"
              testId="widget-total"
            />
            <WidgetCard
              label="Disbursed"
              value={`${currency} ${fmtAmt(w.disbursed)}`}
              sub={`${w.disbursed_count || 0} disbursed`}
              icon={TrendingUp}
              color="emerald"
              testId="widget-disbursed"
            />
            <WidgetCard
              label="Rejected"
              value={`${currency} ${fmtAmt(w.rejected)}`}
              sub={`${w.rejected_count || 0} rejected`}
              icon={XCircle}
              color="red"
              testId="widget-rejected"
            />
            <WidgetCard
              label="Pending"
              value={`${currency} ${fmtAmt(w.pending)}`}
              sub={`${w.pending_count || 0} in pipeline`}
              icon={Clock}
              color="amber"
              testId="widget-pending"
            />
            <WidgetCard
              label="Average"
              value={`${currency} ${fmtAmt(w.average)}`}
              sub="per request"
              icon={BarChart3}
              color="purple"
              testId="widget-average"
            />
            <WidgetCard
              label="Approval Rate"
              value={`${w.approval_rate || 0}%`}
              sub="of decided"
              icon={ArrowUpRight}
              color={w.approval_rate >= 70 ? 'emerald' : w.approval_rate >= 40 ? 'amber' : 'red'}
              testId="widget-approval-rate"
            />
          </div>

          {/* ── Charts Row ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Category */}
            <Card data-testid="chart-by-category">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-blue-500" /> Expenses by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(data?.by_category || []).length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-8">No data for this period</p>
                ) : (
                  <div className="space-y-3">
                    {data.by_category.map((c, i) => {
                      const max = data.by_category[0]?.amount || 1;
                      const pct = Math.round((c.amount / max) * 100);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-zinc-700 dark:text-zinc-300 font-medium">{c.category}</span>
                            <span className="text-zinc-500">{currency} {fmtAmt(c.amount)} <span className="text-xs text-zinc-400">({c.count})</span></span>
                          </div>
                          <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-full h-2">
                            <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* By Department */}
            <Card data-testid="chart-by-department">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-purple-500" /> Expenses by Department
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(data?.by_department || []).length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-8">No data for this period</p>
                ) : (
                  <div className="space-y-3">
                    {data.by_department.map((d, i) => {
                      const max = data.by_department[0]?.amount || 1;
                      const pct = Math.round((d.amount / max) * 100);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-zinc-700 dark:text-zinc-300 font-medium">{d.department}</span>
                            <span className="text-zinc-500">{currency} {fmtAmt(d.amount)} <span className="text-xs text-zinc-400">({d.count})</span></span>
                          </div>
                          <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-full h-2">
                            <div className="h-2 rounded-full bg-purple-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Status Breakdown + Monthly Trend ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Status */}
            <Card data-testid="chart-by-status">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Breakdown by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {(data?.by_status || []).length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-8">No data for this period</p>
                ) : (
                  <div className="space-y-2">
                    {data.by_status.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5">
                        <div className={`w-3 h-3 rounded-full shrink-0 ${STATUS_COLORS[s.status] || 'bg-zinc-400'}`} />
                        <span className="flex-1 text-sm text-zinc-700 dark:text-zinc-300">
                          {STATUS_LABELS[s.status] || s.status}
                        </span>
                        <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{currency} {fmtAmt(s.amount)}</span>
                        <span className="text-xs text-zinc-400">{s.count} req</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Trend */}
            <Card data-testid="chart-monthly-trend">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-500" /> Monthly Trend (6 months)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(data?.monthly_trend || []).length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-8">No data</p>
                ) : (
                  <div className="space-y-3">
                    {data.monthly_trend.map((m, i) => {
                      const maxAmt = Math.max(...data.monthly_trend.map((t) => t.amount), 1);
                      const pct = Math.round((m.amount / maxAmt) * 100);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-zinc-700 dark:text-zinc-300 font-medium">{m.month}</span>
                            <span className="text-zinc-500">{currency} {fmtAmt(m.amount)} <span className="text-xs text-zinc-400">({m.count})</span></span>
                          </div>
                          <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-full h-2">
                            <div className="h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

// ── Widget Card Component ──
const WIDGET_STYLES = {
  blue: 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400',
  emerald: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400',
  red: 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400',
  amber: 'bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400',
  purple: 'bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400',
};

const WidgetCard = ({ label, value, sub, icon: Icon, color, testId }) => (
  <Card className="overflow-hidden" data-testid={testId}>
    <CardContent className="py-4 px-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${WIDGET_STYLES[color] || WIDGET_STYLES.blue}`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-zinc-900 dark:text-zinc-100 truncate" data-testid={`${testId}-value`}>{value}</p>
      <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>
    </CardContent>
  </Card>
);

export default ExpenseAnalytics;
